import type { AppRole } from "@/core/auth/roles";

export type DocumentationAudience = "public" | "user" | "technical" | "admin";

export type DocumentationCategorySlug =
  | "getting-started"
  | "architecture"
  | "features"
  | "developer-guide"
  | "api-reference"
  | "user-guides"
  | "operations"
  | "troubleshooting";

export interface DocumentationManifestEntry {
  category: DocumentationCategorySlug;
  categoryLabel: string;
  slug: string;
  title: string;
  summary: string;
  sourcePath: string;
  audience: DocumentationAudience;
  visibleRoles: AppRole[];
  order: number;
  showInSidebar: boolean;
  showOnHome: boolean;
}

export interface DocumentationManifestCategory {
  slug: DocumentationCategorySlug;
  label: string;
  folder: string;
  audience: DocumentationAudience;
  order: number;
}

export interface DocumentationNavPage {
  id: string;
  slug: string;
  title: string;
  summary: string;
  sourcePath: string;
  audience: DocumentationAudience;
  visibleRoles: AppRole[];
  order: number;
  showOnHome: boolean;
}

export interface DocumentationNavCategory {
  category: string;
  slug: DocumentationCategorySlug;
  pages: DocumentationNavPage[];
}

const ALL_ROLES: AppRole[] = [
  "admin",
  "tech_support",
  "agency_owner",
  "company_owner",
  "developer",
  "call_center",
];

const TECHNICAL_ROLES: AppRole[] = ["admin", "tech_support", "developer"];
const BUSINESS_ROLES: AppRole[] = [
  "admin",
  "tech_support",
  "agency_owner",
  "company_owner",
  "developer",
];
const SUPPORT_ROLES: AppRole[] = ["admin", "tech_support"];
const ADMIN_ONLY: AppRole[] = ["admin"];

export const documentationCategories: DocumentationManifestCategory[] = [
  {
    slug: "getting-started",
    label: "Getting Started",
    folder: "1-GETTING-STARTED",
    audience: "public",
    order: 1,
  },
  {
    slug: "architecture",
    label: "Architecture",
    folder: "2-ARCHITECTURE",
    audience: "technical",
    order: 2,
  },
  {
    slug: "features",
    label: "Features",
    folder: "3-FEATURES",
    audience: "user",
    order: 3,
  },
  {
    slug: "developer-guide",
    label: "Developer Guide",
    folder: "4-DEVELOPER-GUIDE",
    audience: "technical",
    order: 4,
  },
  {
    slug: "api-reference",
    label: "API Reference",
    folder: "5-API-REFERENCE",
    audience: "technical",
    order: 5,
  },
  {
    slug: "user-guides",
    label: "User Guides",
    folder: "6-USER-GUIDES",
    audience: "user",
    order: 6,
  },
  {
    slug: "operations",
    label: "Operations",
    folder: "8-OPERATIONS",
    audience: "admin",
    order: 8,
  },
  {
    slug: "troubleshooting",
    label: "Troubleshooting",
    folder: "9-TROUBLESHOOTING",
    audience: "admin",
    order: 9,
  },
];

export const documentationManifest: DocumentationManifestEntry[] = [
  {
    category: "getting-started",
    categoryLabel: "Getting Started",
    slug: "quickstart",
    title: "Quick Start",
    summary: "Learn the fastest path from sign-in to your first working campaign setup.",
    sourcePath: "/docs/1-GETTING-STARTED/QUICKSTART.md",
    audience: "public",
    visibleRoles: ALL_ROLES,
    order: 1,
    showInSidebar: true,
    showOnHome: true,
  },
  {
    category: "getting-started",
    categoryLabel: "Getting Started",
    slug: "overview",
    title: "Platform Overview",
    summary: "Understand what Mobul does and how the main parts fit together.",
    sourcePath: "/docs/1-GETTING-STARTED/OVERVIEW.md",
    audience: "public",
    visibleRoles: ALL_ROLES,
    order: 2,
    showInSidebar: true,
    showOnHome: true,
  },
  {
    category: "getting-started",
    categoryLabel: "Getting Started",
    slug: "first-campaign",
    title: "Launch Your First Campaign",
    summary: "Follow a task-based walkthrough for building and checking a live campaign.",
    sourcePath: "/docs/1-GETTING-STARTED/FIRST_CAMPAIGN.md",
    audience: "public",
    visibleRoles: ALL_ROLES,
    order: 3,
    showInSidebar: true,
    showOnHome: true,
  },
  {
    category: "getting-started",
    categoryLabel: "Getting Started",
    slug: "terminology",
    title: "Terminology",
    summary: "Look up the platform terms used in campaigns, gift cards, forms, and routing.",
    sourcePath: "/docs/1-GETTING-STARTED/TERMINOLOGY.md",
    audience: "public",
    visibleRoles: ALL_ROLES,
    order: 4,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "getting-started",
    categoryLabel: "Getting Started",
    slug: "mvp-setup",
    title: "MVP Setup Checklist",
    summary: "Prepare a minimal platform setup for internal demos and first validation passes.",
    sourcePath: "/docs/1-GETTING-STARTED/MVP_SETUP.md",
    audience: "public",
    visibleRoles: ALL_ROLES,
    order: 5,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "architecture",
    categoryLabel: "Architecture",
    slug: "architecture-overview",
    title: "Architecture Overview",
    summary: "See how the React app, Supabase services, and delivery workflows connect.",
    sourcePath: "/docs/2-ARCHITECTURE/OVERVIEW.md",
    audience: "technical",
    visibleRoles: TECHNICAL_ROLES,
    order: 1,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "architecture",
    categoryLabel: "Architecture",
    slug: "data-model",
    title: "Data Model",
    summary: "Review the main records and relationships behind campaigns, contacts, and rewards.",
    sourcePath: "/docs/2-ARCHITECTURE/DATA_MODEL.md",
    audience: "technical",
    visibleRoles: TECHNICAL_ROLES,
    order: 2,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "architecture",
    categoryLabel: "Architecture",
    slug: "security",
    title: "Security",
    summary: "Understand role-based access, RLS, and operational safeguards.",
    sourcePath: "/docs/2-ARCHITECTURE/SECURITY.md",
    audience: "technical",
    visibleRoles: TECHNICAL_ROLES,
    order: 3,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "architecture",
    categoryLabel: "Architecture",
    slug: "scalability",
    title: "Scalability",
    summary: "Review the scaling assumptions and performance-sensitive workflows.",
    sourcePath: "/docs/2-ARCHITECTURE/SCALABILITY.md",
    audience: "technical",
    visibleRoles: TECHNICAL_ROLES,
    order: 4,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "features",
    categoryLabel: "Features",
    slug: "campaigns",
    title: "Campaigns",
    summary: "Learn how campaigns move from planning to launch and measurement.",
    sourcePath: "/docs/3-FEATURES/CAMPAIGNS.md",
    audience: "user",
    visibleRoles: BUSINESS_ROLES,
    order: 1,
    showInSidebar: true,
    showOnHome: true,
  },
  {
    category: "features",
    categoryLabel: "Features",
    slug: "campaign-lifecycle",
    title: "Campaign Lifecycle",
    summary: "See the full lifecycle from draft through delivery, redemption, and reporting.",
    sourcePath: "/docs/3-FEATURES/CAMPAIGN_LIFECYCLE.md",
    audience: "user",
    visibleRoles: BUSINESS_ROLES,
    order: 2,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "features",
    categoryLabel: "Features",
    slug: "audiences",
    title: "Audiences",
    summary: "Manage contacts, lists, segments, and import flows.",
    sourcePath: "/docs/3-FEATURES/AUDIENCES.md",
    audience: "user",
    visibleRoles: BUSINESS_ROLES,
    order: 3,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "features",
    categoryLabel: "Features",
    slug: "gift-cards",
    title: "Gift Cards",
    summary: "Understand inventory, brand access, fulfillment, and customer redemption.",
    sourcePath: "/docs/3-FEATURES/GIFT_CARDS.md",
    audience: "user",
    visibleRoles: BUSINESS_ROLES,
    order: 4,
    showInSidebar: true,
    showOnHome: true,
  },
  {
    category: "features",
    categoryLabel: "Features",
    slug: "landing-pages",
    title: "Landing Pages",
    summary: "Build and manage web experiences that support campaigns and reward delivery.",
    sourcePath: "/docs/3-FEATURES/LANDING_PAGES.md",
    audience: "user",
    visibleRoles: BUSINESS_ROLES,
    order: 5,
    showInSidebar: true,
    showOnHome: true,
  },
  {
    category: "features",
    categoryLabel: "Features",
    slug: "analytics",
    title: "Analytics",
    summary: "Track activity, outcomes, and performance across campaigns and forms.",
    sourcePath: "/docs/3-FEATURES/ANALYTICS.md",
    audience: "user",
    visibleRoles: BUSINESS_ROLES,
    order: 6,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "features",
    categoryLabel: "Features",
    slug: "code-enrichment",
    title: "Code Enrichment",
    summary: "Review how imported data can be cleaned up and enriched before use.",
    sourcePath: "/docs/3-FEATURES/CODE_ENRICHMENT.md",
    audience: "user",
    visibleRoles: BUSINESS_ROLES,
    order: 7,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "features",
    categoryLabel: "Features",
    slug: "lead-marketplace",
    title: "Lead Marketplace",
    summary: "Understand the marketplace workflow and how lead sources connect to campaigns.",
    sourcePath: "/docs/3-FEATURES/LEAD_MARKETPLACE.md",
    audience: "user",
    visibleRoles: BUSINESS_ROLES,
    order: 8,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "features",
    categoryLabel: "Features",
    slug: "purl-qr-codes",
    title: "PURL and QR Codes",
    summary: "See how recipients reach personalized destinations and how that activity is tracked.",
    sourcePath: "/docs/3-FEATURES/PURL_QR_CODES.md",
    audience: "user",
    visibleRoles: BUSINESS_ROLES,
    order: 9,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "developer-guide",
    categoryLabel: "Developer Guide",
    slug: "setup",
    title: "Development Setup",
    summary: "Set up the local app, environment variables, and Supabase connections.",
    sourcePath: "/docs/4-DEVELOPER-GUIDE/SETUP.md",
    audience: "technical",
    visibleRoles: TECHNICAL_ROLES,
    order: 1,
    showInSidebar: true,
    showOnHome: true,
  },
  {
    category: "developer-guide",
    categoryLabel: "Developer Guide",
    slug: "edge-functions",
    title: "Edge Functions",
    summary: "Learn where server-side workflows live and how they are deployed.",
    sourcePath: "/docs/4-DEVELOPER-GUIDE/EDGE_FUNCTIONS.md",
    audience: "technical",
    visibleRoles: TECHNICAL_ROLES,
    order: 2,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "developer-guide",
    categoryLabel: "Developer Guide",
    slug: "database",
    title: "Database",
    summary: "Review migrations, RLS-sensitive areas, and data maintenance practices.",
    sourcePath: "/docs/4-DEVELOPER-GUIDE/DATABASE.md",
    audience: "technical",
    visibleRoles: TECHNICAL_ROLES,
    order: 3,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "developer-guide",
    categoryLabel: "Developer Guide",
    slug: "deployment",
    title: "Deployment",
    summary: "Understand the deployment flow, checkpoints, and verification steps.",
    sourcePath: "/docs/4-DEVELOPER-GUIDE/DEPLOYMENT.md",
    audience: "technical",
    visibleRoles: TECHNICAL_ROLES,
    order: 4,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "developer-guide",
    categoryLabel: "Developer Guide",
    slug: "testing",
    title: "Testing",
    summary: "Run the main validation paths and interpret the expected results.",
    sourcePath: "/docs/4-DEVELOPER-GUIDE/TESTING.md",
    audience: "technical",
    visibleRoles: TECHNICAL_ROLES,
    order: 5,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "developer-guide",
    categoryLabel: "Developer Guide",
    slug: "testing-campaigns",
    title: "Testing Campaigns",
    summary: "Validate campaign-specific flows before release or troubleshooting work.",
    sourcePath: "/docs/4-DEVELOPER-GUIDE/TESTING_CAMPAIGNS.md",
    audience: "technical",
    visibleRoles: TECHNICAL_ROLES,
    order: 6,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "developer-guide",
    categoryLabel: "Developer Guide",
    slug: "email-setup",
    title: "Email Setup",
    summary: "Configure sender settings and email delivery dependencies.",
    sourcePath: "/docs/4-DEVELOPER-GUIDE/EMAIL_SETUP.md",
    audience: "technical",
    visibleRoles: TECHNICAL_ROLES,
    order: 7,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "developer-guide",
    categoryLabel: "Developer Guide",
    slug: "event-tracking",
    title: "Event Tracking",
    summary: "Follow how user activity and delivery events are recorded across the platform.",
    sourcePath: "/docs/4-DEVELOPER-GUIDE/EVENT_TRACKING.md",
    audience: "technical",
    visibleRoles: TECHNICAL_ROLES,
    order: 8,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "developer-guide",
    categoryLabel: "Developer Guide",
    slug: "demo-data",
    title: "Demo Data",
    summary: "Use repeatable demo and test data safely in local or staging environments.",
    sourcePath: "/docs/4-DEVELOPER-GUIDE/DEMO_DATA.md",
    audience: "technical",
    visibleRoles: TECHNICAL_ROLES,
    order: 9,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "developer-guide",
    categoryLabel: "Developer Guide",
    slug: "production-checklist",
    title: "Production Checklist",
    summary: "Review release gates before touching production systems.",
    sourcePath: "/docs/4-DEVELOPER-GUIDE/PRODUCTION_CHECKLIST.md",
    audience: "technical",
    visibleRoles: TECHNICAL_ROLES,
    order: 10,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "api-reference",
    categoryLabel: "API Reference",
    slug: "edge-functions-api",
    title: "Edge Functions API",
    summary: "See the server-side endpoints that power platform workflows.",
    sourcePath: "/docs/5-API-REFERENCE/EDGE_FUNCTIONS.md",
    audience: "technical",
    visibleRoles: TECHNICAL_ROLES,
    order: 1,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "api-reference",
    categoryLabel: "API Reference",
    slug: "rest-api",
    title: "REST API",
    summary: "Review the current external API shape, limits, and supported use cases.",
    sourcePath: "/docs/5-API-REFERENCE/REST_API.md",
    audience: "technical",
    visibleRoles: TECHNICAL_ROLES,
    order: 2,
    showInSidebar: true,
    showOnHome: true,
  },
  {
    category: "api-reference",
    categoryLabel: "API Reference",
    slug: "authentication",
    title: "Authentication",
    summary: "Understand how authentication and role-sensitive access control work.",
    sourcePath: "/docs/5-API-REFERENCE/AUTHENTICATION.md",
    audience: "technical",
    visibleRoles: TECHNICAL_ROLES,
    order: 3,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "api-reference",
    categoryLabel: "API Reference",
    slug: "webhooks",
    title: "Webhooks",
    summary: "Learn what event notifications exist and how to consume them safely.",
    sourcePath: "/docs/5-API-REFERENCE/WEBHOOKS.md",
    audience: "technical",
    visibleRoles: TECHNICAL_ROLES,
    order: 4,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "api-reference",
    categoryLabel: "API Reference",
    slug: "examples",
    title: "Examples",
    summary: "Copy common integration patterns without having to invent the flow yourself.",
    sourcePath: "/docs/5-API-REFERENCE/EXAMPLES.md",
    audience: "technical",
    visibleRoles: TECHNICAL_ROLES,
    order: 5,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "user-guides",
    categoryLabel: "User Guides",
    slug: "admin-guide",
    title: "Admin Guide",
    summary: "Learn the operational tasks that keep the platform healthy and usable.",
    sourcePath: "/docs/6-USER-GUIDES/ADMIN_GUIDE.md",
    audience: "user",
    visibleRoles: ADMIN_ONLY,
    order: 1,
    showInSidebar: true,
    showOnHome: true,
  },
  {
    category: "user-guides",
    categoryLabel: "User Guides",
    slug: "agency-guide",
    title: "Agency Guide",
    summary: "See how agencies manage clients, campaigns, and team coordination.",
    sourcePath: "/docs/6-USER-GUIDES/AGENCY_GUIDE.md",
    audience: "user",
    visibleRoles: ["admin", "tech_support", "agency_owner"],
    order: 2,
    showInSidebar: true,
    showOnHome: true,
  },
  {
    category: "user-guides",
    categoryLabel: "User Guides",
    slug: "client-guide",
    title: "Client Guide",
    summary: "Follow the day-to-day workflow for company teams running campaigns.",
    sourcePath: "/docs/6-USER-GUIDES/CLIENT_GUIDE.md",
    audience: "user",
    visibleRoles: ["admin", "tech_support", "agency_owner", "company_owner"],
    order: 3,
    showInSidebar: true,
    showOnHome: true,
  },
  {
    category: "user-guides",
    categoryLabel: "User Guides",
    slug: "call-center-guide",
    title: "Call Center Guide",
    summary: "Handle redemptions, caller verification, and escalation steps with less guesswork.",
    sourcePath: "/docs/6-USER-GUIDES/CALL_CENTER_GUIDE.md",
    audience: "user",
    visibleRoles: ["admin", "tech_support", "agency_owner", "company_owner", "call_center"],
    order: 4,
    showInSidebar: true,
    showOnHome: true,
  },
  {
    category: "operations",
    categoryLabel: "Operations",
    slug: "operations-index",
    title: "Operations Overview",
    summary: "Start here for production support, recovery, and system-care procedures.",
    sourcePath: "/docs/8-OPERATIONS/INDEX.md",
    audience: "admin",
    visibleRoles: SUPPORT_ROLES,
    order: 1,
    showInSidebar: true,
    showOnHome: true,
  },
  {
    category: "troubleshooting",
    categoryLabel: "Troubleshooting",
    slug: "troubleshooting-index",
    title: "Troubleshooting Overview",
    summary: "Use this index to narrow issues before escalating or editing data.",
    sourcePath: "/docs/9-TROUBLESHOOTING/INDEX.md",
    audience: "admin",
    visibleRoles: SUPPORT_ROLES,
    order: 1,
    showInSidebar: true,
    showOnHome: true,
  },
  {
    category: "troubleshooting",
    categoryLabel: "Troubleshooting",
    slug: "troubleshooting-gift-cards",
    title: "Troubleshooting Gift Cards",
    summary: "Investigate inventory, assignment, and redemption problems safely.",
    sourcePath: "/docs/9-TROUBLESHOOTING/GIFT_CARDS.md",
    audience: "admin",
    visibleRoles: SUPPORT_ROLES,
    order: 2,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "troubleshooting",
    categoryLabel: "Troubleshooting",
    slug: "troubleshooting-sms",
    title: "Troubleshooting SMS Delivery",
    summary: "Check the most common reasons messages fail or arrive unexpectedly.",
    sourcePath: "/docs/9-TROUBLESHOOTING/SMS_DELIVERY.md",
    audience: "admin",
    visibleRoles: SUPPORT_ROLES,
    order: 3,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "troubleshooting",
    categoryLabel: "Troubleshooting",
    slug: "troubleshooting-permissions",
    title: "Troubleshooting Permissions",
    summary: "Resolve access problems by checking roles, permissions, and context.",
    sourcePath: "/docs/9-TROUBLESHOOTING/PERMISSIONS.md",
    audience: "admin",
    visibleRoles: SUPPORT_ROLES,
    order: 4,
    showInSidebar: true,
    showOnHome: false,
  },
  {
    category: "troubleshooting",
    categoryLabel: "Troubleshooting",
    slug: "troubleshooting-errors",
    title: "Troubleshooting Common Errors",
    summary: "Map common error states to the next safe troubleshooting step.",
    sourcePath: "/docs/9-TROUBLESHOOTING/COMMON_ERRORS.md",
    audience: "admin",
    visibleRoles: SUPPORT_ROLES,
    order: 5,
    showInSidebar: true,
    showOnHome: false,
  },
];

export function getDocumentationCategory(
  category: DocumentationCategorySlug,
): DocumentationManifestCategory | undefined {
  return documentationCategories.find((item) => item.slug === category);
}

export function getDocumentationCategoryLabel(category: string): string {
  return documentationCategories.find((item) => item.slug === category)?.label ?? category;
}

export function getDocumentationEntry(
  category: string,
  slug: string,
): DocumentationManifestEntry | undefined {
  return documentationManifest.find(
    (item) => item.category === category && item.slug === slug,
  );
}

export function canViewDocumentationEntry(
  entry: DocumentationManifestEntry,
  roles: AppRole[],
): boolean {
  return roles.some((role) => entry.visibleRoles.includes(role));
}

export function getDocumentationHomeEntries(roles: AppRole[]): DocumentationManifestEntry[] {
  return documentationManifest
    .filter((entry) => entry.showOnHome && canViewDocumentationEntry(entry, roles))
    .sort((left, right) => left.order - right.order);
}

export function getDocumentationSidebarEntries(
  roles: AppRole[],
): DocumentationNavCategory[] {
  return documentationCategories
    .map((category) => {
      const pages = documentationManifest
        .filter(
          (entry) =>
            entry.category === category.slug &&
            entry.showInSidebar &&
            canViewDocumentationEntry(entry, roles),
        )
        .sort((left, right) => left.order - right.order)
        .map((entry) => ({
          id: `${entry.category}-${entry.slug}`,
          slug: entry.slug,
          title: entry.title,
          summary: entry.summary,
          sourcePath: entry.sourcePath,
          audience: entry.audience,
          visibleRoles: entry.visibleRoles,
          order: entry.order,
          showOnHome: entry.showOnHome,
        }));

      return {
        category: category.label,
        slug: category.slug,
        pages,
      };
    })
    .filter((category) => category.pages.length > 0);
}
